import { z } from "zod";

/**
 * mybestuser OAuth2 Schemas
 * Types for integrating with mybestuser authentication service
 */

// Admin roles that grant elevated access
export const MYBESTUSER_ADMIN_ROLES = [
  "super",
  "admin",
  "developer",
  "editor",
  "educational_cons",
] as const;

export type MyBestUserAdminRole = (typeof MYBESTUSER_ADMIN_ROLES)[number];

/**
 * Login input for mybestuser signin mutation
 */
export const MyBestUserLoginInputSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required"),
  clientId: z.string().optional(),
});

export type MyBestUserLoginInput = z.infer<typeof MyBestUserLoginInputSchema>;

/**
 * User object returned from mybestuser
 */
export const MyBestUserUserSchema = z.object({
  id: z.string(),
  username: z.string(),
  name: z.string(),
  // Transform empty string to null since mybestuser returns "" for missing emails
  email: z
    .string()
    .transform((val) => (val === "" ? null : val))
    .pipe(z.string().email().nullable())
    .optional()
    .nullable(),
  role: z.string().optional().nullable(),
  group: z.string().optional().nullable(),
  type: z.string().optional(),
  status: z.string().optional(),
  // Transform empty string to null for consistency
  englishName: z
    .string()
    .transform((val) => (val === "" ? null : val))
    .optional()
    .nullable(),
  emailVerified: z.boolean().optional().nullable(),
});

export type MyBestUserUser = z.infer<typeof MyBestUserUserSchema>;

/**
 * Response from mybestuser signin mutation
 */
export const MyBestUserResponseSchema = z.object({
  status: z.number(),
  success: z.boolean(),
  message: z.string(),
  me: MyBestUserUserSchema.optional().nullable(),
  accessToken: z.string().optional().nullable(),
  refreshToken: z.string().optional().nullable(),
  expiresIn: z.number().optional().nullable(),
});

export type MyBestUserResponse = z.infer<typeof MyBestUserResponseSchema>;

/**
 * GraphQL response wrapper
 */
export const MyBestUserGraphQLResponseSchema = z.object({
  data: z
    .object({
      signin: MyBestUserResponseSchema,
    })
    .optional(),
  errors: z
    .array(
      z.object({
        message: z.string(),
      })
    )
    .optional(),
});

export type MyBestUserGraphQLResponse = z.infer<typeof MyBestUserGraphQLResponseSchema>;

/**
 * Helper to check if a role is an admin role
 */
export function isMyBestUserAdmin(role: string | null | undefined): boolean {
  if (!role) return false;
  return MYBESTUSER_ADMIN_ROLES.includes(role as MyBestUserAdminRole);
}

/**
 * User-School membership from MyBestUser
 */
export const SchoolMembershipSchema = z.object({
  id: z.string(),
  userId: z.string(),
  schoolId: z.string(),
  role: z.enum(["principal", "teacher", "student", "secretary"]),
  syncedAt: z.coerce.date(),
});

export type SchoolMembership = z.infer<typeof SchoolMembershipSchema>;

/**
 * School data from MyBestUser API (includes members)
 */
export const MyBestUserSchoolDataSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().nullable(),
  status: z.enum(["activated", "deactivated"]),
  country: z.string().nullable(),
  members: z.array(
    z.object({
      myBestUserId: z.string(),
      role: z.enum(["principal", "teacher", "student", "secretary"]),
    })
  ),
});

export type MyBestUserSchoolData = z.infer<typeof MyBestUserSchoolDataSchema>;
